import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUser } from "aws-amplify/auth";

import { fetchDatasets } from "../../store/datasetSlice";
import { datasetPickVerdict } from "../../utils/historicalModelRuns";
import { datasetWriteTargetProblem } from "../../utils/historicalRuns";
import { OUTPUT_DESTINATIONS, datasetPolicyFor, datasetPolicyMismatch, destinationOf, inputDatasetIdsOf } from "../../utils/outputDestination";
import type { OutputDestination, OutputDestinationValue } from "../../utils/outputDestination";
import type { AppDispatch, RootState } from "../../store";
import type { DatasetFieldContext } from "./DatasetField";

interface OutputDestinationFieldProps {
  fieldKey: string;
  value: OutputDestinationValue;
  onChange: (value: OutputDestinationValue) => void;
  /** Every field's value: a dataset the model reads can't also be one it writes */
  siblingValues?: Record<string, unknown>;
  context?: DatasetFieldContext;
}

/**
 * Where a ticked output of a HISTORICAL template goes (docs/architecture/HISTORICAL_DATA_RUNNERS.md): a new dataset
 * made on deploy (the default, one per output, named "<model> · <output>" unless a name is typed) or an existing
 * historical dataset the model's owner may write. A dataset whose repeated-timestamps policy doesn't suit the
 * template (the quality rules need Replace window; a raw feed doesn't) is offered with a warning, not refused.
 */
const OutputDestinationField: React.FC<OutputDestinationFieldProps> = ({ fieldKey, value, onChange, siblingValues, context }) => {
  const dispatch = useDispatch<AppDispatch>();
  const personal = useSelector((state: RootState) => state.datasets?.personalDatasets || {});
  const organization = useSelector((state: RootState) => state.datasets?.organizationDatasets || {});
  const loading = useSelector((state: RootState) => state.datasets?.loading);
  const organizationsById = useSelector((state: RootState) => state.user?.organizations?.data || {});
  const organizationIdsKey = Object.keys(organizationsById).sort().join(",");
  const destination: OutputDestination = destinationOf(value);

  const readIds = useMemo(() => new Set(inputDatasetIdsOf(siblingValues)), [siblingValues]);
  const datasets = useMemo(() => {
    const all = [...Object.values(personal), ...Object.values(organization)] as any[];
    return all
      .filter((d) => d && d.datasetType === "HISTORICAL" && !d.deletedAt)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [personal, organization]);

  // The wizard doesn't always load datasets; read them once when the store has none and a dataset is wanted
  useEffect(() => {
    if (destination !== "DATASET" || datasets.length || loading === "pending") return;
    let cancelled = false;
    getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        dispatch(fetchDatasets({ userId: user.userId, organizationIds: organizationIdsKey ? organizationIdsKey.split(",") : [] }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, organizationIdsKey]);

  const set = (patch: Partial<OutputDestinationValue>) => onChange({ ...value, value: true, ...patch });
  const picked = destination === "DATASET" ? datasets.find((d) => d.id === value?.datasetId) : null;
  const pickedMismatch = picked ? datasetPolicyMismatch(picked.duplicatePolicy, context?.mergePolicy) : null;
  const newPolicy = datasetPolicyFor(context?.mergePolicy);

  return (
    <div className="mt-2 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-2" data-testid={`output-destination-${fieldKey}`}>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {OUTPUT_DESTINATIONS.map((option) => (
          <label key={option.value} className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="radio"
              name={`${fieldKey}-destination`}
              checked={destination === option.value}
              onChange={() => set({ destination: option.value })}
            />
            {option.label}
          </label>
        ))}
      </div>

      {destination === "DATASET" && (
        <select
          value={value?.datasetId || ""}
          onChange={(e) => set({ datasetId: e.target.value || null })}
          className="eai-select w-full"
          aria-label="Dataset to write"
        >
          <option value="">{loading === "pending" && !datasets.length ? "Loading datasets…" : "Choose a historical dataset"}</option>
          {datasets.map((d) => {
            const refused = datasetPickVerdict({ ...(context ?? {}), datasetOrganizationId: d.organizationId }).refused;
            const reads = readIds.has(d.id);
            const unusable = datasetWriteTargetProblem(d);
            const mismatch = datasetPolicyMismatch(d.duplicatePolicy, context?.mergePolicy);
            return (
              <option key={d.id} value={d.id} disabled={Boolean(refused) || reads || Boolean(unusable)}>
                {d.name}
                {reads ? " · the model reads this dataset" : unusable ? ` · ${unusable}` : refused ? ` · ${refused}` : mismatch ? " · policy differs (see note)" : ""}
              </option>
            );
          })}
        </select>
      )}

      {destination === "NEW_DATASET" && (
        <input
          type="text"
          value={value?.newDatasetName || ""}
          onChange={(e) => set({ newDatasetName: e.target.value })}
          placeholder="New dataset name (blank = model name · output name)"
          className="eai-input w-full"
          aria-label="New dataset name"
        />
      )}

      {pickedMismatch && (
        <p className="text-xs text-amber-700" data-testid={`output-destination-${fieldKey}-warning`}>
          This dataset's repeated-timestamps policy differs: {pickedMismatch}.
        </p>
      )}
      <p className="text-xs text-gray-500">
        {destination === "NEW_DATASET"
          ? newPolicy === "REPLACE_WINDOW"
            ? "Made on deploy with the model's owner and organization. Each run replaces the window it covers, so values the model retracts disappear."
            : "Made on deploy with the model's owner and organization. Each run's rows are loaded into it (newest value wins), where custom views and other models can read them."
          : "Each run's rows are loaded into the dataset, where custom views and other models can read them."}
      </p>
    </div>
  );
};

export default OutputDestinationField;
